import PropTypes from '@root/vendor/prop-types';
import UrlConversionTrackingService from '@root/joinroot.com/src/services/url-conversion-tracking-service';
import environmentalizeUrl from '@root/joinroot.com/src/utils/environmentalize-url';
import { Link as GatsbyLink, navigate } from 'gatsby';
import { StyleSheet } from '@root/core/src/utils/styles';
import { isAnchorLink, isEmailAddressLink, isJoinrootLink, isPdfLink, isPhoneNumberLink } from '@root/joinroot.com/src/utils/detect-link-type';
import { isMobileBrowser } from '@root/core/src/utils/detect-mobile-browser';
import { useEffect, useState } from '@root/vendor/react';

export default function Link({
  children,
  to,
  isInternal = false,
  isTabbable = true,
  onClick,
  environmentalizeUrlFnToUse = environmentalizeUrl,
  ...props
}) {
  const [isInternalToJoinroot, setIsInternalToJoinroot] = useState(false);
  const [isAnchor, setIsAnchor] = useState(false);
  const tabIndex = isTabbable ? '0' : '-1';
  const href = environmentalizeUrlFnToUse(to);

  const isPhoneNumber = isPhoneNumberLink(to);
  const isEmailAddress = isEmailAddressLink(to);

  useEffect(() => {
    const isInternalLink = !(isPdfLink(to) || !isJoinrootLink(to));

    setIsAnchor(isAnchorLink(to));
    setIsInternalToJoinroot(isInternalLink);
  }, [to]);

  const handleClick = (event) => {
    if (isEmailAddress || isPhoneNumber) { return; }

    event.preventDefault();
    onClick && onClick(event);

    if (!to) { return; }

    const targetLocation = href;

    UrlConversionTrackingService.trackConversionForUrl(to);

    if (isAnchor) {
      const sectionId = href.split('#')[1];
      const section = document.getElementById(sectionId);

      if (!sectionId) { return; }

      if (section) {
        section.scrollIntoView({
          behavior: 'smooth',
        });
        return;
      }

      navigate(href);
      return;
    }

    if (isInternal || isInternalToJoinroot) {
      window.setTimeout(() => {
        navigate(href);
      }, 100);
      return;
    }

    window.open(targetLocation);
  };

  function getExternalLinkValues() {
    if (isInternalToJoinroot || isAnchor || isEmailAddress) {
      return {
        target: null,
        rel: null,
      };
    }

    return {
      target: '_blank',
      rel: 'noopener',
    };
  }

  if (isPhoneNumber && !isMobileBrowser()) {
    return (
      <span>{children}</span>
    );
  }

  if (isInternal || isInternalToJoinroot) {
    return (
      <GatsbyLink
        activeClassName={'active'}
        css={styles.link}
        onClick={handleClick}
        tabIndex={tabIndex}
        to={href}
        {...props}
      >
        {children}
      </GatsbyLink>
    );
  }

  return (
    <a
      css={styles.link}
      href={href}
      onClick={handleClick}
      rel={getExternalLinkValues().rel}
      tabIndex={tabIndex}
      target={getExternalLinkValues().target}
      {...props}
    >
      {children}
    </a>
  );
}

const styles = StyleSheet.create({
  link: {
    textDecoration: 'none',
  },
});

Link.propTypes = {
  children: PropTypes.node.isRequired,
  environmentalizeUrlFnToUse: PropTypes.func,
  isInternal: PropTypes.bool,
  isTabbable: PropTypes.bool,
  onClick: PropTypes.func,
  to: PropTypes.string.isRequired,
};
